import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { FaHeartbeat, FaTimes, FaStar } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RatingInput = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-sm text-gray-300 mb-2">{label}</label>
    <div className="flex gap-2" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
            n <= value ? 'bg-pink-500 text-white' : 'bg-white/5 text-gray-500 hover:bg-white/10'
          }`}>
          {n}
        </button>
      ))}
    </div>
  </div>
);

export default function FollowupModal({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    feeling_rating: 3,
    sleep_quality: 3,
    energy_level: 3,
    medication_changes: '',
    new_symptoms: '',
    recent_exams: '',
    pain_areas: '',
    comments: '',
    foods_to_remove: '',
    foods_to_add: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      await axios.post(`${API}/followup/submit`, form, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Seguimiento enviado correctamente');
      onClose();
    } catch (err) {
      toast.error('Error al enviar el seguimiento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        data-testid="followup-modal">
        <motion.div
          initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
          className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto">
          <div className="sticky top-0 bg-[#1a1a1a] p-4 border-b border-white/10 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <FaHeartbeat className="text-pink-500" />
              <h2 className="text-lg font-bold text-white">Seguimiento de Salud</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-followup-btn">
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-5">
            <p className="text-sm text-gray-400">Es momento de actualizar tu estado de salud para ajustar tu plan.</p>

            <RatingInput label="Como te has sentido en general? (1=Mal, 5=Excelente)"
              value={form.feeling_rating} onChange={(v) => setForm(p => ({ ...p, feeling_rating: v }))}
              testId="feeling-rating" />

            <RatingInput label="Calidad de sueno (1=Muy mal, 5=Excelente)"
              value={form.sleep_quality} onChange={(v) => setForm(p => ({ ...p, sleep_quality: v }))}
              testId="sleep-rating" />

            <RatingInput label="Nivel de energia (1=Muy bajo, 5=Muy alto)"
              value={form.energy_level} onChange={(v) => setForm(p => ({ ...p, energy_level: v }))}
              testId="energy-rating" />

            <div>
              <label className="block text-sm text-gray-300 mb-1">Cambios en medicamentos?</label>
              <Textarea placeholder="Algun cambio en medicamentos, dosis, nuevos medicamentos..."
                value={form.medication_changes}
                onChange={(e) => setForm(p => ({ ...p, medication_changes: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="medication-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Nuevos sintomas?</label>
              <Textarea placeholder="Has experimentado sintomas nuevos o diferentes..."
                value={form.new_symptoms}
                onChange={(e) => setForm(p => ({ ...p, new_symptoms: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="symptoms-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Resultados de examenes recientes?</label>
              <Textarea placeholder="Si te has hecho examenes medicos, comparte los resultados..."
                value={form.recent_exams}
                onChange={(e) => setForm(p => ({ ...p, recent_exams: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="exams-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Zonas de dolor o molestia?</label>
              <Textarea placeholder="Alguna zona del cuerpo que te cause dolor o incomodidad..."
                value={form.pain_areas}
                onChange={(e) => setForm(p => ({ ...p, pain_areas: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="pain-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">🚫 Alimentos que quieres quitar de tu dieta</label>
              <Textarea placeholder="Ej: pollo, brócoli, atún... (los que ya no quieres seguir comiendo)"
                value={form.foods_to_remove}
                onChange={(e) => setForm(p => ({ ...p, foods_to_remove: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="foods-to-remove-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">➕ Alimentos que quieres agregar</label>
              <Textarea placeholder="Ej: salmón, quinoa, lentejas... (los que te gustaría incluir)"
                value={form.foods_to_add}
                onChange={(e) => setForm(p => ({ ...p, foods_to_add: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="foods-to-add-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Comentarios adicionales</label>
              <Textarea placeholder="Cualquier otro comentario sobre tu estado de salud..."
                value={form.comments}
                onChange={(e) => setForm(p => ({ ...p, comments: e.target.value }))}
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="comments-input" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose}
                className="flex-1 border-white/10 text-gray-400">
                Despues
              </Button>
              <Button type="submit" disabled={loading}
                className="flex-1 bg-pink-500 hover:bg-pink-600 text-white"
                data-testid="submit-followup-btn">
                {loading ? 'Enviando...' : 'Enviar Seguimiento'}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
